import api from './api';

export interface Event {
  id: string;             // UUID
  session_id?: string | null;
  event_type: string;
  payload: Record<string, unknown>;
  severity: number;       // 0-100
  severity_label: 'info' | 'suspect' | 'high' | 'critical';
  source_ip?: string | null;
  action_taken: string;   // 'log' | 'flag' | 'kick' | 'block'
  created_at: string;
  // Sessão 1 Frente A — fields novos via migration 20260523000200
  attack_category?: string;          // 11 categorias canônicas + UNKNOWN
  source_program?: {
    name?: string;
    signer_subject?: string;
    signer_thumbprint?: string;
    version?: string;
    path_sanitized?: string;
    confidence?: 'high' | 'medium' | 'low';
  } | null;
  dedup_key?: string | null;
  occurrence_count?: number;
  first_seen_at?: string;
  last_seen_at?: string;

  // ── Fase 3 / identidade ──
  // `identity_epoch` É devolvido pelo backend (models/Event.h ToJson) desde a
  // Fase 1: qual época da sessão estava vigente quando o evento foi observado.
  // Época > 0 significa que a sessão TROCOU de identidade — numa lan house,
  // que outra pessoa sentou no PC.
  identity_epoch?: number;

  // ⚠️ O DONO NÃO VEM NO EVENTO, e não é esquecimento: o backend resolve a
  // identidade por época no FILTRO (EventListFilter.owner_user_id /
  // owner_account_id), não no corpo da resposta. Ou seja: quem está listando
  // já sabe de quem é — filtrou por isso. Estes campos ficam opcionais para o
  // dia em que o backend passar a devolvê-los; a UI não depende deles.
  user_id?: string | null;
  account_id?: string | null;
}

export interface ListEventsParams {
  cursor?: number;
  limit?: number;
  // Erro 65 fix (2026-05-11) — agora alinhado com backend contract:
  // severity range numeric (0-100) em vez de label.
  // event_type exato em vez de "type" wildcard.
  severity_min?: number;       // 0..100
  severity_max?: number;       // 0..100
  event_type?: string;          // ex: 'SUSPICIOUS_MODULE_LOADED'
  session_uuid?: string;        // filter por session
  from?: string;                 // ISO timestamp
  to?: string;                   // ISO timestamp
  // Convenience: passa label e a função traduz pra range numeric.
  severity?: 'info' | 'suspect' | 'high' | 'critical';
  // Sessão 1 Frente A — filters novos
  category?: string;             // attack_category exata
  source_program_name?: string;  // ex: "wpe pro.exe"
  aggregated_only?: boolean;     // só rows com occurrence_count > 1
  min_occurrence_count?: number;

  // ── Fase 3 — o filtro que faltava para banir ──
  // O filtro aceitava session_uuid, tipo, severidade, janela, categoria,
  // programa e contagem — e NÃO aceitava conta nem jogador. Era impossível
  // perguntar "me mostre tudo da conta fulano", que é literalmente a pergunta
  // que precede um ban.
  //
  // Casa contra o dono da ÉPOCA em que o evento foi observado, não contra a
  // identidade atual da sessão (EventListFilter, FASE 3). Filtrar pela
  // identidade atual devolveria, na lan house, os eventos do jogador ANTERIOR
  // junto com os de X — o defeito D1 servido numa tela de busca.
  //
  // ⚠️ Não há filtro por `hwid` neste endpoint. Máquina se investiga em
  // /investigations/hwid/{v}, que já existe.
  account_id?: string;
  user_id?: string;
}

export interface ListEventsResponse {
  data: Event[];
  next_cursor?: number;
}

// Traduz label severity → range numeric (matches backend SeverityLabel logic).
function severityLabelToRange(label?: string): { min?: number; max?: number } {
  switch (label) {
    case 'info':     return { min: 0, max: 29 };
    case 'suspect':  return { min: 30, max: 69 };
    case 'high':     return { min: 70, max: 89 };
    case 'critical': return { min: 90, max: 100 };
    default:         return {};
  }
}

// GET /api/v1/admin/events — Erro 65 fix (2026-05-11) endpoint agora real.
export async function listEvents(params: ListEventsParams = {}): Promise<ListEventsResponse> {
  try {
    // Resolve severity label → range se passado, prefere min/max explícitos
    const rng = severityLabelToRange(params.severity);
    const sev_min = params.severity_min ?? rng.min;
    const sev_max = params.severity_max ?? rng.max;

    const resp = await api.get<ListEventsResponse>('/admin/events', {
      params: {
        cursor: params.cursor || 0,
        limit: params.limit || 50,
        session_uuid: params.session_uuid,
        event_type: params.event_type,
        severity_min: sev_min,
        severity_max: sev_max,
        from: params.from,
        to: params.to,
        // Sessão 1 Frente A — filters novos
        category: params.category,
        source_program_name: params.source_program_name,
        aggregated_only: params.aggregated_only ? 'true' : undefined,
        min_occurrence_count: params.min_occurrence_count,
        // Fase 3 — filtros de identidade, resolvidos por época no backend.
        account_id: params.account_id,
        user_id: params.user_id,
      },
    });
    return resp.data;
  } catch {
    // Falha de rede / 5xx — retorna lista vazia gracioso (UI mantém estado prev)
    return { data: [] };
  }
}

// T3b.5 — Kill switches
export interface KillSwitch {
  id: string;             // UUID
  event_type: string;
  reason: string;
  disabled_by_user_id?: number | null;
  disabled_until?: string | null;
  created_at: string;
  updated_at: string;
}

export async function listKillSwitches(): Promise<{ data: KillSwitch[] }> {
  try {
    const resp = await api.get<{ data: KillSwitch[] }>('/admin/event-kill-switches');
    return resp.data;
  } catch {
    return { data: [] };
  }
}

export async function createKillSwitch(input: {
  event_type: string;
  reason: string;
  disabled_until?: string;
}): Promise<KillSwitch> {
  const resp = await api.post<KillSwitch>('/admin/event-kill-switches', input);
  return resp.data;
}

export async function deleteKillSwitch(uuid: string): Promise<void> {
  await api.delete(`/admin/event-kill-switches/${uuid}`);
}
